<template>
	<div class="select-none">
		<div class="flex items-center justify-between gap-1 mb-5">
			<button
				type="button"
				class="flex items-center justify-center w-10 h-10 border rounded-full bg-neutral-50 border-neutral-100"
				:class="{ 'invisible': !canDecrement }"
				@click="handleCalendarControl('subtract')"
			>
				<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 -960 960 960" class="h-3">
					<path fill="currentColor" d="M655-80 255-480l400-400 56 57-343 343 343 343-56 57Z" />
				</svg>
			</button>
			<div class="font-bold">{{ viewerMonth }}</div>
			<button
				type="button"
				class="flex items-center justify-center w-10 h-10 border rounded-full bg-neutral-50 border-neutral-100"
				:class="{ 'invisible': !canIncrement }"
				@click="handleCalendarControl('add')"
			>
				<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 -960 960 960" class="h-3 rotate-180">
					<path fill="currentColor" d="M655-80 255-480l400-400 56 57-343 343 343 343-56 57Z" />
				</svg>
			</button>
		</div>
		<div class="grid grid-cols-7 gap-1 py-3">
			<div v-for="viewerDay in viewerDays" :key="viewerDay.formattedDay" class="text-sm text-center text-neutral-500">
				{{ viewerDay.formattedDay }}
			</div>
		</div>
		<div class="grid grid-cols-7 gap-1">
			<button
				v-for="viewerDate in viewerDates"
				:key="viewerDate.formattedDay"
				type="button"
				:class="{
          'relative aspect-square items-center justify-center rounded font-semibold': true,
          'opacity-50': viewerDate.isPast,
          'bg-primary-500 text-white': viewerDate.selected && viewerDate.info?.status !== 'swimwear',
          'bg-blue-500 text-white': viewerDate.selected && viewerDate.info?.status === 'swimwear',
          'bg-neutral-50 text-black': !viewerDate.selected  && viewerDate.info?.status !== 'swimwear',
          'bg-neutral-50 text-blue-500': !viewerDate.selected && viewerDate.info?.status === 'swimwear',
          'ring-2 ring-inset ring-blue-500': highlightSwimwearDays && viewerDate.info?.status === 'swimwear',
        }"
				@click="handleSelectDate(viewerDate)"
			>
				{{ viewerDate.day }}
				<svg
					v-if="highlightSwimwearDays && viewerDate.info?.status !== 'swimwear'"
					class="absolute inset-0 size-full pointer-events-none text-neutral-500/30"
					preserveAspectRatio="none"
				>
					<line x1="20%" y1="20%" x2="80%" y2="80%" stroke="currentColor" stroke-width="1" />
					<line x1="80%" y1="20%" x2="20%" y2="80%" stroke="currentColor" stroke-width="1" />
				</svg>
			</button>
		</div>

		<div class="flex items-center justify-between gap-4 mt-2 text-xs">
			<div class="flex items-center gap-1 text-blue-500" :class="{ 'invisible': !showSwimwearDayInfo }">
				<InfoFilledIcon class="h-3.5" />
				{{ t('swimwear_day') }}
			</div>
			<slot name="footer" />
		</div>
	</div>
</template>

<script lang="ts" setup>
import { addMonths, eachDayOfInterval, format, lastDayOfWeek, setDay, startOfWeek, subMonths, startOfToday, isAfter, isBefore, startOfMonth, addYears, endOfMonth } from 'date-fns';
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useLocalizedDate } from '../useLocalizedDate';
import { useApi } from '../api';
import InfoFilledIcon from '@thermenresorts/icons/svg/info-filled.svg';

export type Props = {
  language: string
  workspaceId?: number
  modelValue: string[]
  highlightSwimwearDays?: boolean
  onDaysSelected?: (days: string[]) => void
  onMonthChanged?: () => void
}

export type CalendarDateInfo = {
  date: string
  available: boolean
  status: 'default' | 'swimwear'
}

export type ViewerDate = {
  formattedDay: string
  isPast: boolean
  isFuture: boolean
  isSelectable: boolean
  selected: boolean
  day: number
  info?: CalendarDateInfo
}

const props = defineProps<Props>();

const emit = defineEmits<{
  (event: 'daysSelected', days: string[]): void
  (event: 'monthChanged'): void
  (event: 'update:modelValue', days: string[]): void
}>();

const { t } = useI18n();
const { localizeDate } = useLocalizedDate();
const api = useApi();

const today = ref(startOfToday());
const calendarFocus = ref(new Date());
const selectedDays = ref([format(new Date(), 'yyyy-MM-dd')]);

const calendarInfo = ref<Record<string, CalendarDateInfo>>({});
const calendarInfoCacheKeys = ref(new Set<string>());

const updateCalendarInfo = () => {
  loadCalendarData(calendarFocus.value);
  loadCalendarData(addMonths(calendarFocus.value, 1));
};

const loadCalendarData = async (date: Date) => {
  const calendarCacheKey = format(date, 'yyyy-MM');
  if (calendarInfoCacheKeys.value.has(calendarCacheKey)) return;

  if (!props.workspaceId) return;

  try {
    const year = parseInt(format(date, 'yyyy'), 10);
    const month = parseInt(format(date, 'MM'), 10)
    const response = await api.calendar.get(props.workspaceId, month, year);
    calendarInfo.value = { ...calendarInfo.value, ...response.data.dates };
    calendarInfoCacheKeys.value.add(calendarCacheKey);
  } catch (error) {
    console.log(error);
  }
};

const handleCalendarControl = (event: 'add' | 'subtract') => {
  calendarFocus.value = event === 'add' ? addMonths(calendarFocus.value, 1) : subMonths(calendarFocus.value, 1);
  updateCalendarInfo();
  emit('monthChanged');
};

const handleSelectDate = (viewerDate: ViewerDate) => {
  if (!viewerDate.isSelectable) return;

  const newState = [viewerDate.formattedDay];

  selectedDays.value = newState;

  emit('update:modelValue', newState);
  emit('daysSelected', newState);
};

onMounted(() => {
  if (selectedDays.value) {
    emit('update:modelValue', selectedDays.value);
  }
});

watch(() => props.modelValue, (value) => {
  if (value && value.length > 0) {
    selectedDays.value = value;
    calendarFocus.value = new Date(value[0]);
    updateCalendarInfo();
  }
}, { immediate: true });

const viewerDates = computed<ViewerDate[]>(() => {
  const month = calendarFocus.value.getMonth();
  const year = calendarFocus.value.getFullYear();
  const startDay = startOfWeek(new Date(year, month, 1), { weekStartsOn: 1 });
  const endDay = lastDayOfWeek(new Date(year, month + 1, 0), { weekStartsOn: 1 });

  return eachDayOfInterval({ start: startDay, end: endDay }).map((date: Date) => {
    const formattedDay = format(date, 'yyyy-MM-dd');
    return {
      formattedDay,
      isPast: date < today.value,
      isFuture: date >= today.value,
      isSelectable: date >= today.value,
      selected: selectedDays.value ? selectedDays.value.includes(formattedDay) : false,
      day: date.getDate(),
      info: calendarInfo.value[formattedDay] ?? undefined,
    } satisfies ViewerDate;
  });
});

const viewerDays = computed(() => {
  return [1, 2, 3, 4, 5, 6, 0].map(number => {
    const day = setDay(new Date(), number);
    const formattedDay = localizeDate(day, 'EEEEEE');
    return { number, formattedDay };
  });
});

const viewerMonth = computed(() => localizeDate(calendarFocus.value, 'MMMM yyyy'));

const canDecrement = computed(() => isAfter(startOfMonth(calendarFocus.value), startOfMonth(today.value)));

const canIncrement = computed(() => isBefore(endOfMonth(calendarFocus.value), addYears(endOfMonth(today.value), 1)));

const showSwimwearDayInfo = computed(() => viewerDates.value.some(x => x.info?.status === 'swimwear'));

defineExpose({ showSwimwearDayInfo });
</script>
